import { Box, Tooltip } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import dayjs, { Dayjs } from "dayjs";
import { memo } from "react";
import { ReviewTimelineTick } from "src/store/api/generatedApi";
import { TimelineSlot } from "./timelineLayout";

const EventMarker = ({
  tick,
  position,
  slot,
}: {
  tick: ReviewTimelineTick;
  position: number;
  slot: TimelineSlot;
}) => {
  const { t } = useTenantTranslation();
  const isArrival = tick.flags.includes("railcar_arrival");
  const isDeparture = tick.flags.includes("railcar_departure");

  const colour = isArrival
    ? "success.main"
    : isDeparture
      ? "error.main"
      : tick.material
        ? "info.main"
        : "grey.500";

  const typeLabel =
    isArrival || isDeparture
      ? t(isArrival ? "inbound" : "outbound")
      : (tick.event_type?.toUpperCase() ?? "");
  const tooltipText = tick.material
    ? [typeLabel, tick.material].filter(Boolean).join(": ")
    : [typeLabel, tick.comment].filter(Boolean).join(" - ");

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        sx={{
          position: "absolute",
          left: `${position}%`,
          width: 2,
          top: slot.top,
          height: slot.height,
          bgcolor: colour,
          opacity: 0.6,
          transform: "translateX(-50%)",
          pointerEvents: "none",
          "&:hover": {
            opacity: 0.9,
          },
        }}
      />
    </Tooltip>
  );
};

interface EventMarkersProps {
  ticks: ReviewTimelineTick[];
  slot: TimelineSlot;
  getPositionPercent: (time: Dayjs) => number;
}

export const EventMarkers = memo(
  ({ ticks, slot, getPositionPercent }: EventMarkersProps) => {
    return (
      <>
        {ticks.map((tick) => {
          const position = getPositionPercent(dayjs(tick.start));
          return (
            <EventMarker
              key={tick.id}
              tick={tick}
              position={position}
              slot={slot}
            />
          );
        })}
      </>
    );
  }
);
EventMarkers.displayName = "EventMarkers";
